import { useState } from 'react';
import { EmptyState, PrimaryButton, SecondaryButton, TextButton } from '@/lib';
import {
  useCreateSubscription,
  useDeleteSubscription,
  useSubscriptions,
  type Subscription,
} from '../queries';

type FormState = {
  name: string;
  assetClass: string;
  channel: 'inbox' | 'email' | 'both';
  severities: Array<'critical' | 'warn' | 'watch'>;
};

const EMPTY_FORM: FormState = {
  name: '',
  assetClass: '',
  channel: 'inbox',
  severities: ['critical', 'warn'],
};

export function SubscriptionsTab() {
  const list = useSubscriptions();
  const create = useCreateSubscription();
  const del = useDeleteSubscription();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  if (list.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load subscriptions"
        body={String(list.error)}
        cta={{ label: 'Retry', onClick: () => list.refetch() }}
      />
    );
  }

  const items = list.data ?? [];

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        name: form.name,
        asset_class: form.assetClass || undefined,
        channel: form.channel,
        severities: form.severities,
      },
      {
        onSuccess: () => {
          setForm(EMPTY_FORM);
          setShowForm(false);
        },
      },
    );
  };

  const toggleSeverity = (s: FormState['severities'][number]) => {
    setForm((prev) => ({
      ...prev,
      severities: prev.severities.includes(s)
        ? prev.severities.filter((x) => x !== s)
        : [...prev.severities, s],
    }));
  };

  return (
    <section className="flex flex-col gap-sp-4">
      <div className="flex items-baseline justify-between">
        <p className="text-fs-meta text-gray-700">
          {items.length} subscription{items.length === 1 ? '' : 's'}
        </p>
        {!showForm ? (
          <PrimaryButton label="+ New rule" onClick={() => setShowForm(true)} />
        ) : null}
      </div>

      {showForm ? (
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-4"
        >
          <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
            Rule name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Pumps in Plant A"
              className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
            />
          </label>
          <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
            Asset class (optional)
            <input
              value={form.assetClass}
              onChange={(e) => setForm({ ...form, assetClass: e.target.value })}
              placeholder="Pump"
              className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
            />
          </label>
          <fieldset className="flex flex-col gap-sp-1">
            <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Severities
            </legend>
            {(['critical', 'warn', 'watch'] as const).map((s) => (
              <label key={s} className="flex items-center gap-sp-2 text-fs-body text-gray-900">
                <input
                  type="checkbox"
                  checked={form.severities.includes(s)}
                  onChange={() => toggleSeverity(s)}
                />
                {s}
              </label>
            ))}
          </fieldset>
          <fieldset className="flex flex-col gap-sp-1">
            <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Channel
            </legend>
            {(['inbox', 'email', 'both'] as const).map((c) => (
              <label key={c} className="flex items-center gap-sp-2 text-fs-body text-gray-900">
                <input
                  type="radio"
                  name="channel"
                  checked={form.channel === c}
                  onChange={() => setForm({ ...form, channel: c })}
                />
                {c}
              </label>
            ))}
          </fieldset>
          <div className="flex items-center gap-sp-3">
            <PrimaryButton
              type="submit"
              label={create.isPending ? 'Saving…' : 'Save rule'}
              disabled={create.isPending}
            />
            <SecondaryButton
              type="button"
              label="Cancel"
              onClick={() => {
                setForm(EMPTY_FORM);
                setShowForm(false);
              }}
            />
            {create.isError ? (
              <span role="alert" className="text-fs-meta text-critical">
                {String(create.error)}
              </span>
            ) : null}
          </div>
        </form>
      ) : null}

      {items.length === 0 && !showForm ? (
        <EmptyState
          variant="empty"
          title="No subscriptions yet"
          body="Create a rule to get notified about specific assets, classes, or severities."
        />
      ) : (
        <ul className="flex flex-col gap-sp-2">
          {items.map((s: Subscription) => (
            <li
              key={s.id}
              className="flex items-start justify-between gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-4 py-sp-3"
            >
              <div>
                <p className="text-fs-body font-medium text-gray-900">{s.name}</p>
                <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
                  {[s.asset_class, s.site_id, s.severities?.join('|')]
                    .filter(Boolean)
                    .join(' · ') || 'all assets'}
                  {' · '}{s.channel}
                </p>
              </div>
              <TextButton
                label={del.isPending ? 'Deleting…' : 'Delete'}
                onClick={() => del.mutate(s.id)}
                disabled={del.isPending}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
